import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  CircularProgress,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  Collapse,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BadgeIcon from "@mui/icons-material/Badge";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PeopleIcon from "@mui/icons-material/People";
import BuildIcon from "@mui/icons-material/Build";
import GavelIcon from "@mui/icons-material/Gavel";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import SecurityIcon from "@mui/icons-material/Security";
import BarChartIcon from "@mui/icons-material/BarChart";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SettingsIcon from "@mui/icons-material/Settings";
import CommentIcon from "@mui/icons-material/Comment";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import NotificationsIcon from "@mui/icons-material/Notifications";
import FolderIcon from "@mui/icons-material/Folder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SaveIcon from "@mui/icons-material/Save";
import api from "../../utils/api";

// ── Role categories ───────────────────────────────────────────────────────────
const ROLE_CATEGORIES = [
  { value: "Administration",     label: "Administration",     icon: AdminPanelSettingsIcon, color: "#c0392b" },
  { value: "Staff",              label: "Staff",              icon: BadgeIcon,              color: "#d4811a" },
  { value: "Board & Committees", label: "Board & Committees", icon: AccountBalanceIcon,     color: "#2f63ad" },
];

const ROLE_TO_CATEGORY = {
  Admin:         "Administration",
  Manager:       "Administration",
  User:          "Staff",
  Employee:      "Staff",
  Board_Members: "Board & Committees",
  ARC_Committee: "Board & Committees",
};

const resolveRoleCategory = (role) =>
  ROLE_TO_CATEGORY[role.RoleName] ||
  ROLE_CATEGORIES.find((c) => c.value === role.category)?.value ||
  "Staff";

const getRoleCategoryMeta = (value) =>
  ROLE_CATEGORIES.find((c) => c.value === value) || ROLE_CATEGORIES[1];

// ── Permission categories ─────────────────────────────────────────────────────
const PERM_CATEGORIES = [
  { value: "Dashboard",              label: "Dashboard",              icon: DashboardIcon,            color: "#2f63ad" },
  { value: "Users & Profiles",       label: "Users & Profiles",       icon: ManageAccountsIcon,       color: "#1a8a5c" },
  { value: "Members",                label: "Members",                icon: PeopleIcon,               color: "#546e7a" },
  { value: "Work Orders",            label: "Work Orders",            icon: BuildIcon,                color: "#d4811a" },
  { value: "Violations",             label: "Violations",             icon: GavelIcon,                color: "#c0392b" },
  { value: "Architectural Requests", label: "Architectural Requests", icon: ArchitectureIcon,         color: "#7b3fa0" },
  { value: "Gate & Security",        label: "Gate & Security",        icon: SecurityIcon,             color: "#0e7c7b" },
  { value: "Reports",                label: "Reports",                icon: BarChartIcon,             color: "#8e44ad" },
  { value: "Billing & Integrations", label: "Billing & Integrations", icon: AccountBalanceWalletIcon, color: "#1565c0" },
  { value: "System Settings",        label: "System Settings",        icon: SettingsIcon,             color: "#607d8b" },
  { value: "Comments",               label: "Comments",               icon: CommentIcon,              color: "#795548" },
  { value: "Elections & Voting",     label: "Elections & Voting",     icon: HowToVoteIcon,            color: "#558b2f" },
  { value: "Marina",                 label: "Marina",                 icon: DirectionsCarIcon,        color: "#00838f" },
  { value: "Notifications",          label: "Notifications",          icon: NotificationsIcon,        color: "#f57c00" },
];

// Maps the API's module field → frontend display category
const MODULE_TO_CATEGORY = {
  dashboard:      'Dashboard',
  work_orders:    'Work Orders',
  violations:     'Violations',
  architectural:  'Architectural Requests',
  members:        'Members',
  gate:           'Gate & Security',
  billing:        'Billing & Integrations',
  reports:        'Reports',
  elections:      'Elections & Voting',
  marina:         'Marina',
  users:          'Users & Profiles',
  settings:       'System Settings',
  comments:       'Comments',
  notifications:  'Notifications',
};

const resolvePermCategory = (perm) =>
  MODULE_TO_CATEGORY[perm.module] ||             // ← API field (primary)
  "System Settings";

const getPermCategoryMeta = (value) =>
  PERM_CATEGORIES.find((c) => c.value === value) ||
  PERM_CATEGORIES.find((c) => c.value === "System Settings") ||
  PERM_CATEGORIES[0];

// ── Component ─────────────────────────────────────────────────────────────────
const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [assignedPermissionIds, setAssignedPermissionIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openRoleCats, setOpenRoleCats] = useState({});
  const [openPermCats, setOpenPermCats] = useState({});
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const selectedRole = roles.find((r) => r.RoleID === selectedRoleId);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await api.get("/roles");
        setRoles(res.data);
        // Auto-expand all role categories
        const cats = {};
        ROLE_CATEGORIES.forEach((c) => (cats[c.value] = true));
        setOpenRoleCats(cats);
      } catch (err) {
        console.error("Error fetching roles:", err);
        setSnack({ open: true, message: "Failed to load roles. Please refresh.", severity: "error" });
      }
    };
    const fetchPerms = async () => {
      try {
        const res = await api.get("/permissions");
        setPermissions(res.data);
        // Auto-expand all permission categories
        const cats = {};
        PERM_CATEGORIES.forEach((c) => (cats[c.value] = true));
        setOpenPermCats(cats);
      } catch (err) {
        console.error("Error fetching permissions:", err);
        setSnack({ open: true, message: "Failed to load permissions. Please refresh.", severity: "error" });
      }
    };
    fetchRoles();
    fetchPerms();
  }, []);

  useEffect(() => {
    if (!selectedRoleId || permissions.length === 0) return;
    setLoading(true);
    api
      .get(`/role-permissions/${selectedRoleId}`)
      .then((res) => setAssignedPermissionIds(res.data?.permissionIds || []))
      .catch(() => setAssignedPermissionIds([]))
      .finally(() => setLoading(false));
  }, [selectedRoleId, permissions]);

  // Group roles by category
  const groupedRoles = useMemo(() => {
    const map = {};
    roles.forEach((r) => {
      const key = resolveRoleCategory(r);
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return ROLE_CATEGORIES.filter((c) => map[c.value]).map((c) => [c.value, map[c.value]]);
  }, [roles]);

  // Group permissions by category
  const groupedPerms = useMemo(() => {
    const map = {};
    permissions.forEach((p) => {
      const key = resolvePermCategory(p);
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return PERM_CATEGORIES.filter((c) => map[c.value]).map((c) => [c.value, map[c.value]]);
  }, [permissions]);

  const handleSavePermissions = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      await api.post(`/roles/${selectedRoleId}/permissions`, {
        permissionIds: assignedPermissionIds,
      });
      setSnack({ open: true, message: "Permissions saved successfully.", severity: "success" });
    } catch (err) {
      console.error("Error saving permissions:", err);
      setSnack({ open: true, message: "Failed to save permissions.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (id) =>
    setAssignedPermissionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAllInCategory = (permsInCat) => {
    const ids = permsInCat.map((p) => p.PermissionID);
    const allOn = ids.every((id) => assignedPermissionIds.includes(id));
    setAssignedPermissionIds((prev) =>
      allOn
        ? prev.filter((id) => !ids.includes(id))
        : [...new Set([...prev, ...ids])]
    );
  };

  // ── Shared style helpers ────────────────────────────────────────────────────
  const panelSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
    overflow: "hidden",
  };

  const CategoryHeader = ({ catValue, items, openState, onToggle, categoryList }) => {
    const meta = categoryList.find((c) => c.value === catValue) || categoryList[0];
    const CatIcon = meta.icon;
    const isOpen = !!openState[catValue];
    return (
      <ListItemButton
        onClick={() => onToggle(catValue)}
        sx={{
          px: 2,
          py: 1,
          backgroundColor: `${meta.color}0d`,
          borderTop: "1px solid rgba(0,0,0,0.05)",
          "&:hover": { backgroundColor: `${meta.color}1a` },
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "8px",
            backgroundColor: `${meta.color}22`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 1.25,
            flexShrink: 0,
          }}
        >
          <CatIcon sx={{ fontSize: 15, color: meta.color }} />
        </Box>
        <ListItemText
          primary={
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#223142" }}>
              {meta.label}
            </Typography>
          }
        />
        <Chip
          label={items.length}
          size="small"
          sx={{
            mr: 0.75,
            height: 20,
            fontSize: "0.73rem",
            fontWeight: 700,
            backgroundColor: `${meta.color}1a`,
            color: meta.color,
            "& .MuiChip-label": { px: 1 },
          }}
        />
        {isOpen
          ? <ExpandMoreIcon sx={{ fontSize: 17, color: "#8a94a6" }} />
          : <ChevronRightIcon sx={{ fontSize: 17, color: "#8a94a6" }} />}
      </ListItemButton>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 3,
        p: { xs: 2, md: 3 },
      }}
    >
      {/* ── Left: Roles ──────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ ...panelSx, width: { xs: "100%", md: 280 }, alignSelf: "flex-start" }}>
        {/* Header */}
        <Box sx={{ px: 2, py: 2, borderBottom: "1px solid rgba(47,99,173,0.08)", backgroundColor: "rgba(47,99,173,0.03)" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
            Roles
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.2 }}>
            {roles.length} role{roles.length !== 1 ? "s" : ""} · {groupedRoles.length} categories
          </Typography>
        </Box>

        <List disablePadding>
          {groupedRoles.map(([catValue, catRoles]) => {
            const isOpen = !!openRoleCats[catValue];
            return (
              <Box key={catValue}>
                <CategoryHeader
                  catValue={catValue}
                  items={catRoles}
                  openState={openRoleCats}
                  onToggle={(v) => setOpenRoleCats((p) => ({ ...p, [v]: !p[v] }))}
                  categoryList={ROLE_CATEGORIES}
                />
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {catRoles.map((role) => {
                      const meta = getRoleCategoryMeta(catValue);
                      const isSelected = selectedRoleId === role.RoleID;
                      return (
                        <ListItemButton
                          key={role.RoleID}
                          onClick={() => setSelectedRoleId(role.RoleID)}
                          selected={isSelected}
                          sx={{
                            pl: 4,
                            pr: 2,
                            py: 1.1,
                            borderLeft: `3px solid ${isSelected ? meta.color : "transparent"}`,
                            "&.Mui-selected": { backgroundColor: `${meta.color}12` },
                            "&.Mui-selected:hover": { backgroundColor: `${meta.color}1e` },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontSize: "0.88rem",
                                  fontWeight: isSelected ? 700 : 500,
                                  color: isSelected ? "#223142" : "#3c4a5e",
                                }}
                              >
                                {role.RoleName}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ color: "#8a94a6" }}>
                                {role.Description}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Paper>

      {/* ── Right: Permissions ───────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ ...panelSx, flex: 1, minWidth: 0 }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.25,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.03)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
              {selectedRole ? `Permissions. ${selectedRole.RoleName}` : "Select a role"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.2 }}>
              {selectedRole
                ? `${assignedPermissionIds.length} of ${permissions.length} permissions enabled`
                : "Choose a role from the left to manage its permissions."}
            </Typography>
          </Box>

          {selectedRoleId && (
            <Button
              variant="contained"
              onClick={handleSavePermissions}
              disabled={saving}
              startIcon={<SaveIcon />}
              sx={{
                backgroundColor: "#2f63ad",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
              }}
            >
              {saving ? "Saving..." : "Save Permissions"}
            </Button>
          )}
        </Box>

        {/* Body */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !selectedRoleId ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
            <Typography sx={{ color: "#9eaab5", fontWeight: 600 }}>
              No role selected
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowY: "auto", maxHeight: "calc(100vh - 220px)" }}>
            {groupedPerms.map(([catValue, catPerms]) => {
              const meta = getPermCategoryMeta(catValue);
              const CatIcon = meta.icon;
              const isOpen = !!openPermCats[catValue];
              const enabledCount = catPerms.filter((p) =>
                assignedPermissionIds.includes(p.PermissionID)
              ).length;
              const allEnabled = enabledCount === catPerms.length;

              return (
                <Box key={catValue}>
                  {/* Permission category header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 2.5,
                      py: 1.1,
                      backgroundColor: `${meta.color}0d`,
                      borderTop: "1px solid rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: `${meta.color}18` },
                    }}
                    onClick={() =>
                      setOpenPermCats((p) => ({ ...p, [catValue]: !p[catValue] }))
                    }
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "8px",
                        backgroundColor: `${meta.color}22`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1.25,
                        flexShrink: 0,
                      }}
                    >
                      <CatIcon sx={{ fontSize: 15, color: meta.color }} />
                    </Box>

                    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#223142", flex: 1 }}>
                      {meta.label}
                    </Typography>

                    <Chip
                      label={`${enabledCount} / ${catPerms.length}`}
                      size="small"
                      sx={{
                        mr: 1,
                        height: 20,
                        fontSize: "0.73rem",
                        fontWeight: 700,
                        backgroundColor: enabledCount > 0 ? `${meta.color}1a` : "rgba(0,0,0,0.06)",
                        color: enabledCount > 0 ? meta.color : "#8a94a6",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />

                    {/* Toggle all in category */}
                    <Switch
                      size="small"
                      checked={allEnabled}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleAllInCategory(catPerms);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ mr: 0.5 }}
                    />

                    {isOpen
                      ? <ExpandMoreIcon sx={{ fontSize: 17, color: "#8a94a6" }} />
                      : <ChevronRightIcon sx={{ fontSize: 17, color: "#8a94a6" }} />}
                  </Box>

                  {/* Individual permissions */}
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box>
                      {catPerms.map((perm, idx) => {
                        const enabled = assignedPermissionIds.includes(perm.PermissionID);
                        return (
                          <Box
                            key={perm.PermissionID}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              pl: 6.5,
                              pr: 2.5,
                              py: 1.1,
                              borderBottom:
                                idx < catPerms.length - 1 ? "1px solid rgba(0,0,0,0.04)" : "none",
                              backgroundColor: enabled ? `${meta.color}06` : "transparent",
                              transition: "background-color 0.15s",
                              "&:hover": { backgroundColor: `${meta.color}0e` },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                color: enabled ? "#223142" : "#5a6578",
                                fontWeight: enabled ? 600 : 400,
                                pr: 2,
                              }}
                            >
                              {perm.Description}
                            </Typography>
                            <Switch
                              size="small"
                              checked={enabled}
                              onChange={() => togglePermission(perm.PermissionID)}
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": { color: meta.color },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                  backgroundColor: meta.color,
                                },
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RolesPage;
